import { useState } from 'react';
import { MAX_LISTING_IMAGES, FEATURED_MARKET_CODES } from '../../config/constants';
import { getCountry, inferCountryFromLocation, resolveListingCountryClient } from '../../data/countries';
import {
  resolveShareStructureClient,
  validateRaisingStructureClient,
  filterNumericInput,
  sha256File,
  uploadListingPhotos,
  uploadListingVideo,
  emptyRentCostFormFields,
  emptyNearbyFormFields,
  buildRentMonthlyCostsApiBody,
  buildNearbyApiBody,
} from '../../utils/submissions';
import { buildOfferingPropertyPatch } from '../../utils/properties';
import apiClient from '../../utils/api';
import PropertyLocationMap from '../shared/PropertyLocationMap';
import RentMonthlyCostsFields from '../forms/RentMonthlyCostsFields';
import NearbyAmenitiesFields from '../forms/NearbyAmenitiesFields';
import AdminOfferingDetailsFields from '../forms/AdminOfferingDetailsFields';
import {
  getYouTubeThumbnail,
  getYouTubeWatchUrl,
  isYouTubeUrl,
  normalizeYouTubeUrl,
} from '../../utils/youtube';

const FormSection = ({ icon, title, children }) => (
  <section className="list-form-section" style={{ marginBottom: '1.5rem' }}>
    <h3 className="list-form-section-title"><i className={`fas fa-${icon}`}></i>{title}</h3>
    {children}
  </section>
);

const defaultForm = () => ({
  title: '',
  location: '',
  country: 'US',
  type: 'Residential',
  listingMode: 'sale',
  price: '',
  platformPrice: '',
  raisingMode: 'co_owners',
  shareCount: '10',
  minInvestment: '500',
  projectedReturn: '10',
  description: '',
  yearBuilt: '',
  sqft: '',
  beds: '',
  baths: '',
  monthlyRent: '',
  occupancy: '',
  annualOperatingExpenses: '',
  renovationNeeded: 'no',
  renovationCost: '',
  renovationDesc: '',
  landBanking: 'no',
  hot: 'no',
  agentName: '',
  agentCompany: '',
  agentEmail: '',
  agentPhone: '',
  fundingDeadline: '',
  projectedTimeline: '',
  riskRating: '',
  secOfferingType: '',
  propertyManagerName: '',
  propertyManagerLicenseReference: '',
  developerName: '',
  developerTrackRecord: '',
  ...emptyRentCostFormFields(),
  ...emptyNearbyFormFields(),
});

const AdminAddPropertyPanel = ({ addToast, onPublished, userEmail = '' }) => {
  const [form, setForm] = useState(() => ({
    ...defaultForm(),
    agentEmail: userEmail || '',
  }));
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState('');
  const [videoFileName, setVideoFileName] = useState('');
  const [youtubeInput, setYoutubeInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showRentMonthlyCosts, setShowRentMonthlyCosts] = useState(false);
  const [showOfferingDetails, setShowOfferingDetails] = useState(false);
  const [showNearby, setShowNearby] = useState(false);

  const update = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));
  const isRent = form.listingMode === 'rent';
  const priceNum = parseFloat(form.price) || 0;
  const sharePreview = resolveShareStructureClient({
    price: parseFloat(form.platformPrice) || priceNum,
    raisingMode: form.raisingMode,
    shareCount: form.shareCount,
    minInvestment: form.minInvestment,
  });

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    const room = MAX_LISTING_IMAGES - photos.length;
    if (room <= 0) {
      addToast(`Maximum ${MAX_LISTING_IMAGES} photos per listing.`, 'error');
      return;
    }
    const existingHashes = new Set(photos.map((p) => p.hash));
    const toUpload = [];
    for (const file of files) {
      if (toUpload.length >= room) break;
      const hash = await sha256File(file);
      if (existingHashes.has(hash)) {
        addToast(`"${file.name}" is already in this listing.`, 'error');
        continue;
      }
      existingHashes.add(hash);
      toUpload.push({ file, hash });
    }
    if (!toUpload.length) return;
    setUploading(true);
    try {
      const urls = await uploadListingPhotos(toUpload.map((t) => t.file));
      const added = urls.map((url, i) => ({ url, hash: toUpload[i]?.hash || url }));
      setPhotos((prev) => [...prev, ...added].slice(0, MAX_LISTING_IMAGES));
      addToast(`${urls.length} photo(s) uploaded.`);
    } catch (err) {
      addToast(err.message || 'Photo upload failed.', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (url) => setPhotos((prev) => prev.filter((p) => p.url !== url));

  const processVideoFile = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    try {
      const url = await uploadListingVideo(file);
      setVideo(url);
      setVideoFileName(file.name || 'property-video.mp4');
      setYoutubeInput('');
      addToast('Property video uploaded.');
    } catch (err) {
      addToast(err.message || 'Video upload failed.', 'error');
    } finally {
      setUploadingVideo(false);
    }
  };

  const applyYouTubeLink = () => {
    const normalized = normalizeYouTubeUrl(youtubeInput);
    if (!normalized) {
      addToast('Enter a valid YouTube link (youtube.com or youtu.be).', 'error');
      return;
    }
    setVideo(normalized);
    setVideoFileName('YouTube video');
    addToast('YouTube link added.');
  };

  const removeVideo = () => {
    setVideo('');
    setVideoFileName('');
    setYoutubeInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (photos.length === 0) {
      addToast('Upload at least one property photo.', 'error');
      return;
    }
    const listerPrice = parseFloat(form.price);
    const platformPrice = parseFloat(form.platformPrice) || listerPrice;
    if (!form.title.trim() || !form.location.trim() || !listerPrice) {
      addToast('Title, location, and lister price are required.', 'error');
      return;
    }
    if (isRent) {
      const monthlyRent = parseFloat(form.monthlyRent);
      if (!monthlyRent) {
        addToast('Base monthly rent is required for rental listings.', 'error');
        return;
      }
    } else {
      const check = validateRaisingStructureClient({
        price: platformPrice,
        raisingMode: form.raisingMode,
        shareCount: form.shareCount,
        minInvestment: form.minInvestment,
      });
      if (!check.ok) {
        addToast(check.message, 'error');
        return;
      }
    }

    setSubmitting(true);
    try {
      const country = resolveListingCountryClient(form.country, form.location);
      const gallery = photos.map((p) => p.url);
      const cover = gallery[0];
      const renovationNeeded = form.renovationNeeded === 'yes';
      const rentCosts = isRent ? buildRentMonthlyCostsApiBody(form) : {};
      const offeringPatch = !isRent ? buildOfferingPropertyPatch(form) : {};

      let shares = 0;
      let minInvestment = parseFloat(form.minInvestment) || 500;
      let raisingMode = form.raisingMode;
      let shareCount = parseInt(form.shareCount, 10);
      if (!isRent) {
        const resolved = resolveShareStructureClient({
          price: platformPrice,
          raisingMode: form.raisingMode,
          shareCount: form.shareCount,
          minInvestment: form.minInvestment,
        });
        shares = resolved.shares;
        minInvestment = resolved.minInvestment;
        raisingMode = resolved.raisingMode;
        shareCount = resolved.shareCount ?? shareCount;
      }

      const payload = {
        title: form.title.trim(),
        location: form.location.trim(),
        country,
        type: form.type,
        listingMode: isRent ? 'rent' : 'sale',
        listerPrice,
        price: platformPrice,
        description: form.description.trim() || form.title.trim(),
        image: cover,
        gallery,
        images: gallery,
        video: video || '',
        status: 'funding',
        occupancy: parseFloat(form.occupancy) || 0,
        annualOperatingExpenses: parseFloat(form.annualOperatingExpenses) || 0,
        docs: ['Offering Memorandum'],
        hot: form.hot === 'yes',
        landBanking: form.landBanking === 'yes',
        ...buildNearbyApiBody(form),
        renovation: {
          needed: renovationNeeded,
          cost: parseFloat(form.renovationCost) || 0,
          description: form.renovationDesc || '',
        },
        agentName: form.agentName.trim(),
        agentCompany: form.agentCompany.trim(),
        agentEmail: form.agentEmail.trim(),
        agentPhone: form.agentPhone.trim(),
        yearBuilt: parseInt(form.yearBuilt, 10) || 2020,
        sqft: parseInt(form.sqft, 10) || 1000,
        beds: parseInt(form.beds, 10) || 2,
        baths: parseInt(form.baths, 10) || 2,
        monthlyRent: parseFloat(form.monthlyRent) || 0,
        visibleOnSite: true,
        ...(!isRent
          ? {
              shares,
              sharesSold: 0,
              minInvestment,
              raisingMode,
              shareCount,
              projectedReturn: parseFloat(form.projectedReturn) || 10,
              ...offeringPatch,
            }
          : rentCosts),
      };

      await apiClient.createProperty(payload);
      addToast(
        isRent
          ? 'Rental listing published — it will appear on the Rent page.'
          : 'Property published — it will appear on the main Properties page.'
      );
      setForm({ ...defaultForm(), agentEmail: userEmail || '' });
      setPhotos([]);
      setVideo('');
      setVideoFileName('');
      setYoutubeInput('');
      setShowRentMonthlyCosts(false);
      setShowOfferingDetails(false);
      setShowNearby(false);
      onPublished?.();
    } catch (err) {
      addToast(err.message || 'Failed to publish property.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormSection icon="sliders-h" title="Listing type">
        <div className="submission-mode-filters">
          {[
            { key: 'sale', label: 'Fractional sale (invest)', icon: 'chart-pie' },
            { key: 'rent', label: 'For rent', icon: 'key' },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              type="button"
              className={`submission-mode-filter${form.listingMode === key ? ' active' : ''}`}
              onClick={() => update('listingMode', key)}
            >
              <i className={`fas fa-${icon}`} style={{ marginRight: '0.35rem' }}></i>
              {label}
            </button>
          ))}
        </div>
      </FormSection>

      <FormSection icon="home" title="Property information">
        <div className="form-row">
          <div className="form-group">
            <label>Property title</label>
            <input required value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="e.g. Downtown Luxury Condo" />
          </div>
          <div className="form-group">
            <label>Location</label>
            <input
              required
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              onBlur={() => {
                const inferred = inferCountryFromLocation(form.location);
                if (inferred && inferred !== form.country) update('country', inferred);
              }}
              placeholder="City, state, country"
            />
          </div>
        </div>
        <PropertyLocationMap location={form.location} height={220} title="Location preview" />
        <div className="form-row" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label>Country</label>
            <select value={form.country} onChange={(e) => update('country', e.target.value)}>
              {FEATURED_MARKET_CODES.map((code) => (
                <option key={code} value={code}>{getCountry(code).name} ({code})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Property type</label>
            <select value={form.type} onChange={(e) => update('type', e.target.value)}>
              {['Residential', 'Commercial', 'Vacation', 'Land', 'Mixed-Use'].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{isRent ? 'Property value ($)' : 'Lister ask price ($)'}</label>
            <input
              required
              inputMode="decimal"
              value={form.price}
              onChange={(e) => update('price', filterNumericInput(e.target.value, { decimal: true }))}
            />
          </div>
          {!isRent && (
            <div className="form-group">
              <label>Platform price ($)</label>
              <input
                inputMode="decimal"
                placeholder="Defaults to lister price"
                value={form.platformPrice}
                onChange={(e) => update('platformPrice', filterNumericInput(e.target.value, { decimal: true }))}
              />
            </div>
          )}
        </div>
        {!isRent && (
          <>
            <div className="list-v2-inset list-v2-inset--accent" style={{ marginTop: '1rem' }}>
              <h3 className="list-v2-inset__title" style={{ marginBottom: '0.75rem' }}>
                <i className="fas fa-chart-pie"></i> Share structure
              </h3>
              <div className="list-v2-radio-group">
                <label className="list-v2-radio">
                  <input
                    type="radio"
                    name="adminRaisingMode"
                    checked={form.raisingMode !== 'min_investment'}
                    onChange={() => update('raisingMode', 'co_owners')}
                  />
                  <span>
                    <strong>Number of co-owners</strong>
                    <small>e.g. 10 people each buy one share</small>
                  </span>
                </label>
                <label className="list-v2-radio">
                  <input
                    type="radio"
                    name="adminRaisingMode"
                    checked={form.raisingMode === 'min_investment'}
                    onChange={() => update('raisingMode', 'min_investment')}
                  />
                  <span>
                    <strong>Minimum investment</strong>
                    <small>e.g. $25,000 per investor</small>
                  </span>
                </label>
              </div>
              {form.raisingMode === 'min_investment' ? (
                <div className="form-group">
                  <label>Minimum investment per share ($)</label>
                  <input
                    inputMode="decimal"
                    value={form.minInvestment}
                    onChange={(e) => update('minInvestment', filterNumericInput(e.target.value, { decimal: true }))}
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Number of co-owners (total shares)</label>
                  <input
                    inputMode="numeric"
                    value={form.shareCount}
                    onChange={(e) => update('shareCount', filterNumericInput(e.target.value))}
                  />
                </div>
              )}
              {(parseFloat(form.platformPrice) || priceNum) > 0 && (
                <p className="list-v2-share-preview">
                  Preview: {sharePreview.shares.toLocaleString()} shares at about $
                  {((parseFloat(form.platformPrice) || priceNum) / sharePreview.shares).toLocaleString(undefined, { maximumFractionDigits: 0 })} each
                </p>
              )}
            </div>
            <div className="form-row" style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label>Projected return (%)</label>
                <input
                  inputMode="decimal"
                  value={form.projectedReturn}
                  onChange={(e) => update('projectedReturn', filterNumericInput(e.target.value, { decimal: true }))}
                />
              </div>
            </div>
          </>
        )}
        <div className="form-group">
          <label>Description</label>
          <textarea required rows={3} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Describe the property..." />
        </div>
      </FormSection>

      <FormSection icon="images" title="Property photos">
        <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Upload at least one photo (up to {MAX_LISTING_IMAGES}).
        </p>
        <label className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: uploading ? 'wait' : 'pointer' }}>
          <i className={`fas fa-${uploading ? 'spinner fa-spin' : 'cloud-upload-alt'}`}></i>
          {uploading ? 'Uploading…' : 'Add photos'}
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={uploading || photos.length >= MAX_LISTING_IMAGES} onChange={handleImages} />
        </label>
        <span style={{ display: 'block', marginTop: '0.65rem', color: 'var(--text-light)', fontSize: '0.85rem' }}>{photos.length} / {MAX_LISTING_IMAGES} photos</span>
        {photos.length > 0 && (
          <div className="list-photo-grid" style={{ marginTop: '1rem' }}>
            {photos.map((p) => (
              <div key={p.url} className="list-photo-thumb">
                <img src={p.url} alt="" />
                <button type="button" className="list-photo-remove" onClick={() => removeImage(p.url)} aria-label="Remove photo">×</button>
              </div>
            ))}
          </div>
        )}
      </FormSection>

      <FormSection icon="video" title="Property video (optional)">
        <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Upload a walkthrough file or paste a YouTube link — same as the public list form.
        </p>
        {!video ? (
          <>
            <label className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: uploadingVideo ? 'wait' : 'pointer' }}>
              <i className={`fas fa-${uploadingVideo ? 'spinner fa-spin' : 'film'}`}></i>
              {uploadingVideo ? 'Uploading…' : 'Upload video file'}
              <input
                type="file"
                accept=".mp4,.mov,.avi,.mkv,.mpg,.mpeg,video/*"
                hidden
                disabled={uploadingVideo}
                onChange={async (e) => {
                  await processVideoFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            <div className="list-v2-or-divider" aria-hidden="true"><span>or</span></div>
            <div className="list-v2-youtube-input-row">
              <input
                className="list-v2-input"
                type="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeInput}
                onChange={(e) => setYoutubeInput(e.target.value)}
              />
              <button type="button" className="btn btn-primary list-v2-youtube-add" onClick={applyYouTubeLink} disabled={!youtubeInput.trim()}>
                <i className="fab fa-youtube"></i> Add link
              </button>
            </div>
          </>
        ) : isYouTubeUrl(video) ? (
          <div className="list-v2-video-preview list-v2-video-preview--youtube">
            <a href={getYouTubeWatchUrl(video)} target="_blank" rel="noopener noreferrer" className="list-v2-youtube-preview">
              <img src={getYouTubeThumbnail(video)} alt="" />
              <span className="list-v2-youtube-preview__overlay"><i className="fab fa-youtube"></i><span>Watch on YouTube</span></span>
            </a>
            <div className="list-v2-video-preview__bar">
              <p className="list-v2-video-preview__name">{videoFileName}</p>
              <button type="button" className="list-v2-video-remove" onClick={removeVideo}><i className="fas fa-trash-alt"></i> Remove</button>
            </div>
          </div>
        ) : (
          <div className="list-v2-video-preview">
            <video src={video} controls preload="metadata" />
            <div className="list-v2-video-preview__bar">
              <p className="list-v2-video-preview__name">{videoFileName}</p>
              <button type="button" className="list-v2-video-remove" onClick={removeVideo}><i className="fas fa-trash-alt"></i> Remove</button>
            </div>
          </div>
        )}
      </FormSection>

      <FormSection icon="ruler-combined" title="Property details">
        <div className="form-row">
          <div className="form-group"><label>Year built</label><input inputMode="numeric" value={form.yearBuilt} onChange={(e) => update('yearBuilt', filterNumericInput(e.target.value))} /></div>
          <div className="form-group"><label>Square feet</label><input inputMode="numeric" value={form.sqft} onChange={(e) => update('sqft', filterNumericInput(e.target.value))} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Bedrooms</label><input inputMode="numeric" value={form.beds} onChange={(e) => update('beds', filterNumericInput(e.target.value))} /></div>
          <div className="form-group"><label>Bathrooms</label><input inputMode="numeric" value={form.baths} onChange={(e) => update('baths', filterNumericInput(e.target.value))} /></div>
          <div className="form-group">
            <label>Base monthly rent{isRent ? ' (required)' : ''}</label>
            <input inputMode="decimal" value={form.monthlyRent} onChange={(e) => update('monthlyRent', filterNumericInput(e.target.value, { decimal: true }))} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Occupancy rate (%)</label>
            <input inputMode="decimal" placeholder="e.g. 94" value={form.occupancy} onChange={(e) => update('occupancy', filterNumericInput(e.target.value, { decimal: true }))} />
          </div>
          <div className="form-group">
            <label>Est. annual operating expenses ($)</label>
            <input inputMode="decimal" placeholder="e.g. 12000" value={form.annualOperatingExpenses} onChange={(e) => update('annualOperatingExpenses', filterNumericInput(e.target.value, { decimal: true }))} />
          </div>
          <div className="form-group">
            <label>Featured (hot) listing?</label>
            <select value={form.hot} onChange={(e) => update('hot', e.target.value)}><option value="no">No</option><option value="yes">Yes</option></select>
          </div>
        </div>
      </FormSection>

      <FormSection icon="map-marker-alt" title="Nearby landmarks (optional)">
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowNearby((v) => !v)}>
          <i className={`fas fa-${showNearby ? 'chevron-up' : 'plus'}`}></i>
          {showNearby ? 'Hide nearby amenities' : 'Add nearby amenities'}
        </button>
        {showNearby && (
          <div style={{ marginTop: '1rem' }}>
            <NearbyAmenitiesFields form={form} update={update} />
          </div>
        )}
      </FormSection>

      {isRent && (
        <FormSection icon="receipt" title="Monthly costs (optional)">
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowRentMonthlyCosts((v) => !v)}>
            <i className={`fas fa-${showRentMonthlyCosts ? 'chevron-up' : 'plus'}`}></i>
            {showRentMonthlyCosts ? 'Hide monthly costs' : 'Add monthly costs breakdown'}
          </button>
          {showRentMonthlyCosts && (
            <div style={{ marginTop: '1rem' }}>
              <RentMonthlyCostsFields form={form} setForm={setForm} idPrefix="admin-rent-costs" />
            </div>
          )}
        </FormSection>
      )}

      <FormSection icon="tools" title="Renovation & land banking">
        <div className="form-row">
          <div className="form-group">
            <label>Renovation needed?</label>
            <select value={form.renovationNeeded} onChange={(e) => update('renovationNeeded', e.target.value)}><option value="no">No</option><option value="yes">Yes</option></select>
          </div>
          {form.renovationNeeded === 'yes' && (
            <div className="form-group">
              <label>Estimated cost ($)</label>
              <input inputMode="decimal" value={form.renovationCost} onChange={(e) => update('renovationCost', filterNumericInput(e.target.value, { decimal: true }))} />
            </div>
          )}
        </div>
        {form.renovationNeeded === 'yes' && (
          <div className="form-group">
            <label>Renovation description</label>
            <textarea value={form.renovationDesc} onChange={(e) => update('renovationDesc', e.target.value)} />
          </div>
        )}
        <div className="form-group">
          <label>Land banking opportunity?</label>
          <select value={form.landBanking} onChange={(e) => update('landBanking', e.target.value)}><option value="no">No</option><option value="yes">Yes</option></select>
        </div>
      </FormSection>

      {!isRent && (
        <FormSection icon="clipboard-list" title="Offering details (optional)">
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowOfferingDetails((v) => !v)}>
            <i className={`fas fa-${showOfferingDetails ? 'chevron-up' : 'plus'}`}></i>
            {showOfferingDetails ? 'Hide offering details' : 'Add offering details'}
          </button>
          {showOfferingDetails && (
            <div style={{ marginTop: '1rem' }}>
              <AdminOfferingDetailsFields form={form} setForm={setForm} idPrefix="admin-add-offering" />
            </div>
          )}
        </FormSection>
      )}

      <FormSection icon="user-tie" title="Agent / lister contact">
        <div className="form-row">
          <div className="form-group"><label>Agent name</label><input value={form.agentName} onChange={(e) => update('agentName', e.target.value)} placeholder="Full name" /></div>
          <div className="form-group"><label>Company</label><input value={form.agentCompany} onChange={(e) => update('agentCompany', e.target.value)} placeholder="Realty company" /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Email</label><input type="email" value={form.agentEmail} onChange={(e) => update('agentEmail', e.target.value)} placeholder="agent@company.com" /></div>
          <div className="form-group"><label>Phone</label><input value={form.agentPhone} onChange={(e) => update('agentPhone', e.target.value)} placeholder="(555) 123-4567" /></div>
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-light)', margin: 0 }}>
          Agent email is used for CoOwn chat — buyers message this address on listing pages.
        </p>
      </FormSection>

      <button type="submit" className="btn btn-primary" disabled={submitting || uploading || uploadingVideo || photos.length === 0} style={{ marginTop: '0.5rem' }}>
        {submitting ? 'Publishing…' : isRent ? 'Publish rental listing' : 'Publish to main page'}
      </button>
    </form>
  );
};

export default AdminAddPropertyPanel;
